import {vec3, quat} from "_src/libs/gl-matrix";

import TargetControl from "_src/client/controls/target_control";
import utils from "_src/client/controls/utils";
import {RotationIndicator} from "_src/client/widgets/rotation_indicator";

const _vec3Tmp = vec3.create();
const _vec3Tmp2 = vec3.create();
const _vec3Tmp3 = vec3.create();

const _quatTmp = quat.create();

export default class TrackballControl extends TargetControl {

  constructor(object) {

    super(object);

    this.trackVec = vec3.create();
    this.trackCursor = [0, 0];
    this.originQuat = quat.create();
    this.rotationStarted = false;
    this.rotationIndicatorWidget = new RotationIndicator(object);
  }

  updateRotationIndicator() {
    this.rotationIndicatorWidget.update();
  }

  registerRotationIndicator(scene) {
    this.rotationIndicatorWidget.register(scene);
  }

  unregisterRotationIndicator(scene) {
    this.rotationIndicatorWidget.unregister(scene);
  }

  lookAt(x, y, z) {
    super.lookAt(x,y,z);
    this.rotationIndicatorWidget.setVisibility(false);
  }

  setTargetVisibility(visibility) {
    this.rotationIndicatorWidget.setVisibility(visibility);
  }

  setTarget(point) {

    if (!this.enabled) {
      return;
    }

    const trans = this._getObjectTrans();
    const direction = vec3.sub(_vec3Tmp, point, trans);
    const dist = vec3.length(direction);
    vec3.scale(direction, direction, 1.0 / dist);

    vec3.copy(this.target, point);
    this.rotationIndicatorWidget.move(point);

    const camPos = vec3.copy(vec3.create(), trans);
    this.setPosition(camPos);

  }

  startRotation(x, y) {

    const object = this.object;

    if (!this.enabled) {
      return;
    }

    this.trackCursor[0] = x;
    this.trackCursor[1] = y;

    calcTrackballVec(x, y, object.aspect, this.trackVec);
    quat.copy(this.originQuat, object.quat);

    const trans = this._getObjectTrans();

    vec3.subtract(this.direction, this.target, trans);
    vec3.normalize(this.direction, this.direction);

    this.rotationStarted = true;

  }

  rotate(dx, dy) {

    if(!this.rotationStarted) {
      return;
    }

    const object = this.object;

    if (!this.enabled) {
      return;
    }

    const x = this.trackCursor[0] + dx;
    const y = this.trackCursor[1] + dy;

    const newTrack = calcTrackballVec(x, y, object.aspect, _vec3Tmp);

    const delta = vec3.sub(_vec3Tmp2, newTrack, this.trackVec);
    let si = vec3.length(delta) / 2.0;

    const axis = vec3.cross(_vec3Tmp3, newTrack, this.trackVec);
    const phi = si * 0.5 * Math.PI;

    vec3.normalize(axis, axis);

    const rotationView = quat.setAxisAngle(_quatTmp, axis, phi);
    quat.normalize(rotationView, rotationView);

    object.copyRotation(quat.multiply(object.quat, this.originQuat, rotationView));

    const rotationWorld = quat.conjugate(_quatTmp, this.originQuat);
    quat.multiply(rotationWorld, object.quat, rotationWorld);
    const toTarget = vec3.transformQuat(_vec3Tmp, this.direction, rotationWorld);

    const [horAngle, vertAngle] = utils.anglesFromView(toTarget);

    this.horAngle = horAngle;
    this.vertAngle = vertAngle;

    this.trackCursor[0] = x;
    this.trackCursor[1] = y;

    this.updatePosition();

  }

  pan(dX, dY) {
    const object = this.object;

    if (!this.enabled) {
      return;
    }

    let objX = vec3.set(_vec3Tmp, 1, 0, 0);
    vec3.transformQuat(objX, objX, object.quat);

    const objMY = vec3.cross(_vec3Tmp2, this.direction, objX);

    const c = 0.05;
    vec3.scale(objX, objX, -dX * this.dist * c);
    vec3.scale(objMY, objMY, -dY * this.dist * c);

    const objTrans = this._getObjectTrans();
    const trans = vec3.add(_vec3Tmp, objX, objMY);

    const resultTrans = vec3.add(trans, objTrans, trans);

    this._updateDirection(resultTrans);

    const [horAngle, vertAngle] = utils.anglesFromView(this.direction);
    this.horAngle = horAngle;
    this.vertAngle = vertAngle;

    this.dist = vec3.distance(resultTrans, this.target);

    this.updatePosition();
  }

  endRotation() {

    super.endRotation();

    this.rotationStarted = false;

  }

  moveToMinmax(minmax, rect, animation, onStart, onUpdate, onFinish, time = 0.2) {

    if (!this.enabled) {
      return;
    }

    this.rotationIndicatorWidget.setVisibility(false);

    const target = vec3.fromValues(0.5 * (minmax[0] + minmax[3]),
                                   0.5 * (minmax[1] + minmax[4]),
                                   0.5 * (minmax[2] + minmax[5]));

    super.moveToMinmax(minmax, rect, animation, onStart, onUpdate, onFinish, time, target);

  }

}

function calcTrackballVec(x, y, aspect, out) {

  out[0] = x * aspect;
  out[1] = y;
  out[2] = 0;

  const radius = 1;

  const d = vec3.dot(out, out);
  if (d < 0.5) {
    out[2] = Math.sqrt(radius * radius - d);
  } else {
    out[2] = 0.5 / Math.sqrt(d);
  }

  return out;

}